/**
 * Interval Tracking Debug Component
 * WebView'da interval tracking'in durumunu görsel olarak gösterir
 */

import React, { useState, useEffect } from 'react';
import { useWebView } from '../integrations/webview';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useTestStore } from '../store/useTestStore';
import { intervalTrackingTester, runQuickIntervalTest } from '../utils/intervalTrackingTest';
import { formatIntervalsForDisplay, extractTotalDuration } from '../utils/intervalValidation';

interface IntervalDebugInfo {
  questionId: string;
  sessionCount: number;
  totalDuration: number;
  hasActiveSession: boolean;
  lastUpdate: string;
  intervals: string[];
}

export const IntervalTrackingDebug: React.FC = () => {
  const { isWebView } = useWebView();
  const { questionDataCache } = useWhiteboardStore();
  const { questions, currentQuestionIndex } = useTestStore();
  
  const [debugInfo, setDebugInfo] = useState<IntervalDebugInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [testResults, setTestResults] = useState<string>('');

  const currentQuestion = questions[currentQuestionIndex];

  // Update debug info periodically
  useEffect(() => {
    if (!currentQuestion) return;

    const updateDebugInfo = () => {
      const questionData = questionDataCache.get(currentQuestion.questionId || '');
      
      if (questionData?.trajectory) {
        const trajectory = questionData.trajectory;
        const hasActiveSession = trajectory.length > 0 && !trajectory[trajectory.length - 1].endDate;
        
        // Create mock intervals string for display
        const mockIntervals = JSON.stringify(trajectory.map(session => ({
          start: new Date(session.startDate).toISOString().replace('T', ' ').substring(0, 19),
          end: session.endDate ? new Date(session.endDate).toISOString().replace('T', ' ').substring(0, 19) : null,
          duration: session.duration || (session.endDate ? (session.endDate - session.startDate) / 1000 : null)
        })));

        setDebugInfo({
          questionId: currentQuestion.questionId || '',
          sessionCount: trajectory.length,
          totalDuration: questionData.totalTime || 0,
          hasActiveSession,
          lastUpdate: new Date(questionData.updatedAt).toLocaleTimeString(),
          intervals: formatIntervalsForDisplay(mockIntervals)
        });
      } else {
        setDebugInfo({
          questionId: currentQuestion.questionId || '',
          sessionCount: 0,
          totalDuration: 0,
          hasActiveSession: false,
          lastUpdate: 'Never',
          intervals: []
        });
      }
    };

    updateDebugInfo();
    const interval = setInterval(updateDebugInfo, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isWebView, currentQuestion, questionDataCache]);

  // Run test
  const handleRunTest = async () => {
    if (!currentQuestion) return;

    try {
      const result = await runQuickIntervalTest(currentQuestion.questionId || '');
      const report = intervalTrackingTester.generateReport();
      setTestResults(report);
    } catch (error) {
      setTestResults(`Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Toggle visibility with keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'I') {
        setIsVisible(!isVisible);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isVisible]);

  // Show in both WebView and normal mode for debugging
  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 bg-black bg-opacity-90 text-white p-4 rounded-lg shadow-lg z-50 max-w-md">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-yellow-400">🔍 Interval Debug</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-white"
        >
          ✕
        </button>
      </div>

      {debugInfo && (
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-blue-400">Question:</span> {debugInfo.questionId.substring(0, 8)}...
          </div>
          
          <div>
            <span className="text-blue-400">Sessions:</span> {debugInfo.sessionCount}
          </div>
          
          <div>
            <span className="text-blue-400">Total Duration:</span> {debugInfo.totalDuration.toFixed(2)}s
          </div>
          
          <div>
            <span className="text-blue-400">Active Session:</span> 
            <span className={debugInfo.hasActiveSession ? 'text-green-400' : 'text-red-400'}>
              {debugInfo.hasActiveSession ? ' ✅ Yes' : ' ❌ No'}
            </span>
          </div>
          
          <div>
            <span className="text-blue-400">Last Update:</span> {debugInfo.lastUpdate}
          </div>

          {debugInfo.intervals.length > 0 && (
            <div>
              <span className="text-blue-400">Sessions:</span>
              <div className="ml-2 text-xs text-gray-300 max-h-32 overflow-y-auto">
                {debugInfo.intervals.map((interval, index) => (
                  <div key={index}>{interval}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 space-y-2">
        <button
          onClick={handleRunTest}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
        >
          Run Test
        </button>

        {testResults && (
          <div className="bg-gray-800 p-2 rounded text-xs max-h-40 overflow-y-auto">
            <pre className="whitespace-pre-wrap">{testResults}</pre>
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-gray-400">
        Press Ctrl+Shift+I to toggle
      </div>
    </div>
  );
};

// Helper component to show interval status in toolbar
export const IntervalStatusIndicator: React.FC = () => {
  const { isWebView } = useWebView();
  const { questionDataCache } = useWhiteboardStore();
  const { questions, currentQuestionIndex } = useTestStore();
  
  const [hasActiveSession, setHasActiveSession] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  useEffect(() => {
    if (!currentQuestion) return;

    const checkActiveSession = () => {
      const questionData = questionDataCache.get(currentQuestion.questionId || '');
      const trajectory = questionData?.trajectory || [];
      const active = trajectory.length > 0 && !trajectory[trajectory.length - 1].endDate;
      setHasActiveSession(active);
    };

    checkActiveSession();
    const interval = setInterval(checkActiveSession, 1000);

    return () => clearInterval(interval);
  }, [isWebView, currentQuestion, questionDataCache]);

  // Show in both modes for debugging
  // if (!isWebView) {
  //   return null;
  // }

  return (
    <div className="flex items-center space-x-1 text-xs">
      <div 
        className={`w-2 h-2 rounded-full ${hasActiveSession ? 'bg-green-500' : 'bg-red-500'}`}
        title={hasActiveSession ? 'Session active' : 'No active session'}
      />
      <span className="text-gray-600">
        {hasActiveSession ? 'Recording' : 'Idle'}
      </span>
    </div>
  );
};